import {
  cancelBookingAction,
  checkAnswerResponseSchema,
  CheckAnswerRequest,
  CheckAnswerResponse,
  createPodoClassAction,
  createPodoPrestudyRoomAction,
  ensureRegularClassAction,
  levelUpSkipResponseSchema,
  selectLevelAction,
  skipPreStudyAction,
} from '@entities/lesson'
import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { type ClassLangType, TrialLevel } from '@server/schemas'
import { z } from 'zod'

export const lessonEntityMutations = {
  skipPreStudy: ({ bearerToken }: RequestQueryOptions) => ({
    mutationKey: [bearerToken],
    mutationFn: async (classCourseId: number) => {
      return await skipPreStudyAction({ classCourseId }, bearerToken)
    },
  }),
  selectLevel: ({ bearerToken, langType }: RequestQueryOptions<{ langType: ClassLangType }>) => ({
    mutationKey: [bearerToken, langType],
    mutationFn: async (level: TrialLevel) => {
      return await selectLevelAction({ langType, level }, bearerToken)
    },
  }),
  createClass: ({ bearerToken }: RequestQueryOptions) => ({
    mutationKey: [bearerToken],
    mutationFn: async (request: { classCourseId: number; sessionId: string }) => {
      // Use consolidated lesson.action.ts instead of duplicated logic
      return await createPodoClassAction(request, bearerToken)
    },
  }),
  createPrestudyRoom: ({ bearerToken }: RequestQueryOptions) => ({
    mutationKey: [bearerToken, 'createPrestudyRoom'],
    mutationFn: async (request: { classCourseId: number | string; sessionId: string }) => {
      return await createPodoPrestudyRoomAction(request, bearerToken)
    },
  }),

  ensureRegularClass: ({ bearerToken }: RequestQueryOptions) => ({
    mutationKey: [bearerToken, 'ensureRegularClass'],
    mutationFn: async (request: { classCourseId: number }) => {
      return await ensureRegularClassAction(request, bearerToken)
    },
  }),

  cancelBooking: ({ bearerToken }: RequestQueryOptions) => ({
    mutationKey: [bearerToken],
    mutationFn: async (classId: number) => {
      // Use consolidated lesson.action.ts instead of duplicated logic
      return await cancelBookingAction({ classId }, bearerToken)
    },
  }),

  checkAnswer: ({ bearerToken, funnelStep }: RequestQueryOptions<{ funnelStep: string }>) => ({
    mutationKey: [bearerToken, funnelStep],
    mutationFn: async (request: CheckAnswerRequest): Promise<CheckAnswerResponse> => {
      const response = await podoFetcher<CheckAnswerResponse>({
        url: `/api/v1/diagnosis/checkAnswer`,
        method: 'POST',
        body: request,
        header: {
          Authorization: `Bearer ${bearerToken}`,
        },
      })

      return checkAnswerResponseSchema.parse(response)
    },
  }),

  characterChatSkipLevelUp: ({ bearerToken }: RequestQueryOptions) => ({
    mutationKey: [bearerToken],
    mutationFn: async () => {
      return await podoFetcher<z.infer<typeof levelUpSkipResponseSchema>>({
        url: `/api/v2/lecture/ai/level/up/skip`,
        method: 'POST',
        header: {
          Authorization: `Bearer ${bearerToken}`,
        },
      })
    },
  }),

  updateTicketCount: ({ bearerToken }: RequestQueryOptions) => ({
    mutationKey: [bearerToken],
    mutationFn: async (requestDto: {
      reqUpdatePodoTicketCountDtoList: {
        id: string
        langType: string
        purchasedCount: number
        eventType: string
        paymentLangType: string
        lessonTime: number
      }[]
    }) => {
      return await podoFetcher({
        url: `/api/v1/ticket/podo/updateCount`,
        method: 'POST',
        body: requestDto,
        header: {
          Authorization: `Bearer ${bearerToken}`,
        },
      })
    },
  }),
}
